import { useCallback, useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import { IconDownload } from "@tabler/icons-react";
import {
  Badge,
  Button,
  Card,
  Flex,
  Group,
  Pagination,
  SimpleGrid,
  Skeleton,
  Stack,
  Tabs,
  Text,
} from "@mantine/core";
import DataTable from "../../../../../common/components/DataTable/DataTable";
import { fetcher } from "../../../../../services/fetcher";
import {
  errorNotifications,
  successNotifications,
} from "../../../../../utils/handleNotifications";
import { downloadBlob } from "../../../../policies/utils/allPolicyDownload";
import {
  OPERATOR_PORTAL,
  operatorPortalUrl,
} from "../../../../dashboard/api/operatorPortalEndpoints";
import {
  canonicalQueryString,
  DASHBOARD_OPERATOR_SWR_CONFIG,
} from "../../../../dashboard/utils/dashboardQueryKeys";
import styles from "./SpecificOperatorWallet.module.css";

const TAB_TRANSACTIONS = "transactions";
const TAB_RECHARGE = "recharge";

const buildWalletQuery = ({ page, search, tab }) => {
  const params = new URLSearchParams();
  if (page > 1) params.set("page", String(page));
  const term = search?.trim();
  if (term) params.set("search", term);
  if (tab === TAB_RECHARGE) params.set("section", "recharge");
  return canonicalQueryString(params);
};

const currencySymbol = (currency) => (currency === "INR" ? "₹" : currency || "₹");

const formatMoney = (amount, currency = "INR") =>
  `${currencySymbol(currency)}${Number(amount || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const SpecificOperatorWallet = () => {
  const [activeTab, setActiveTab] = useState(TAB_TRANSACTIONS);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setPage(1);
  }, [activeTab, debouncedSearch]);

  const walletQuery = useMemo(
    () => buildWalletQuery({ page, search: debouncedSearch, tab: activeTab }),
    [page, debouncedSearch, activeTab]
  );

  const walletUrl = useMemo(
    () => operatorPortalUrl(OPERATOR_PORTAL.WALLET, walletQuery),
    [walletQuery]
  );

  const { data, error, isLoading } = useSWR(
    walletUrl,
    fetcher,
    DASHBOARD_OPERATOR_SWR_CONFIG
  );

  const summary = data?.summary ?? {};
  const currency = summary.currency || "INR";

  const activeSection = useMemo(() => {
    if (activeTab === TAB_RECHARGE) return data?.recharge_history ?? {};
    return data?.transactions ?? {};
  }, [activeTab, data]);

  const filteredRows = useMemo(() => {
    const results = Array.isArray(activeSection?.results) ? activeSection.results : [];
    const query = searchQuery.trim().toLowerCase();
    if (!query) return results;

    return results.filter((row) => {
      const txn = String(row?.transaction_number || row?.txn || "").toLowerCase();
      return txn.includes(query);
    });
  }, [activeSection?.results, searchQuery]);

  const tableRows = useMemo(
    () =>
      filteredRows.map((row, index) => ({
        ...row,
        id: row.transaction_number || row.txn || `wallet-row-${index}`,
        transaction_number: row.transaction_number || row.txn,
        transaction_type: row.transaction_type || row.type,
        created_by_name: row.created_by_name || row.created_by,
        running_balance: formatMoney(row.running_balance, currency),
      })),
    [filteredRows, currency]
  );

  const tableConfig = useMemo(
    () => ({
      fields: [
        { key: "date", label: "Date" },
        { key: "transaction_number", label: "Transaction Number" },
        { key: "transaction_type", label: "Type" },
        {
          key: "amount",
          label: "Amount",
          render: (row) => {
            const type = String(row?.transaction_type || "").toUpperCase();
            const isDeduct = type === "DEDUCT";
            return (
              <Text
                size="sm"
                fw={600}
                c={isDeduct ? "#ef4444" : "#22c55e"}
                component="span"
                my="9px"
              >
                {isDeduct ? "-" : "+"}
                {formatMoney(row?.amount, currency)}
              </Text>
            );
          },
        },
        { key: "running_balance", label: "Running Balance" },
        { key: "created_by_name", label: "Created By" },
        { key: "remarks", label: "Remarks" },
      ],
      searchPlaceholder: "Search transaction number...",
      searchInputWidth: 320,
      showFilterButton: false,
    }),
    [currency]
  );

  const totalCount = activeSection?.count ?? 0;
  const pageSize = activeSection?.page_size || filteredRows.length || 1;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const showingFrom = totalCount ? (page - 1) * pageSize + 1 : 0;
  const showingTo = filteredRows.length ? showingFrom + filteredRows.length - 1 : 0;

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const query = buildWalletQuery({
        page,
        search: debouncedSearch,
        tab: activeTab,
      });
      const exportUrl = operatorPortalUrl(
        OPERATOR_PORTAL.WALLET_TRANSACTIONS_EXPORT,
        query
      );
      const response = await fetcher(
        exportUrl,
        "GET",
        null,
        {},
        { responseType: "arraybuffer" }
      );
      if (!response) throw new Error("Export failed");
      downloadBlob(
        new Blob([response], { type: "text/csv" }),
        "wallet-transactions.csv"
      );
      successNotifications("Transactions exported successfully");
    } catch (err) {
      errorNotifications(err.response?.data || "Failed to export transactions", err);
    } finally {
      setExporting(false);
    }
  }, [page, debouncedSearch, activeTab]);

  if (error) {
    return (
      <Text c="red" ta="center" py="xl">
        Error loading wallet data.
      </Text>
    );
  }

  return (
    <Stack className={styles.page}>
      <div className={styles.header}>
        <Text fz="xl" fw={700}>
          Wallet
        </Text>
        <Text c="dimmed">View balance, recharges, and transaction history.</Text>
      </div>

      <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md" className={styles.statsGrid}>
        <Card withBorder radius="md" p="md" className={styles.statCard}>
          <Skeleton visible={isLoading}>
            <Text className={styles.statLabel}>Current Balance</Text>
            <Text className={styles.statValue} mt="sm">
              {formatMoney(summary.balance, currency)}
            </Text>
          </Skeleton>
        </Card>

        <Card withBorder radius="md" p="md" className={styles.statCard}>
          <Skeleton visible={isLoading}>
            <Text className={styles.statLabel}>Total Recharge</Text>
            <Text className={styles.statValue} mt="sm">
              {formatMoney(summary.total_recharge, currency)}
            </Text>
            {summary.total_recharge_foot ? (
              <Text className={styles.statFoot} mt={4}>
                {summary.total_recharge_foot}
              </Text>
            ) : null}
          </Skeleton>
        </Card>

        <Card withBorder radius="md" p="md" className={styles.statCard}>
          <Skeleton visible={isLoading}>
            <Text className={styles.statLabel}>Wallet Status</Text>
            <Group mt="sm" gap="xs">
              <Badge
                variant="light"
                color={summary.wallet_applicable ? "green" : "gray"}
                size="lg"
                radius="sm"
              >
                {summary.wallet_applicable ? "Active" : "Not applicable"}
              </Badge>
              <Text className={styles.statFoot}>{currency}</Text>
            </Group>
          </Skeleton>
        </Card>
      </SimpleGrid>

      <div className={styles.tablePanel}>
        <Tabs
          value={activeTab}
          onChange={(value) => setActiveTab(value || TAB_TRANSACTIONS)}
        >
          <Tabs.List className={styles.tabsList}>
            <Tabs.Tab value={TAB_TRANSACTIONS} className={styles.tab}>
              All Transactions
            </Tabs.Tab>
            <Tabs.Tab value={TAB_RECHARGE} className={styles.tab}>
              Recharge History
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value={activeTab} pt="md" px="md" pb="md">
            <DataTable
              config={tableConfig}
              data={tableRows}
              searchValue={searchQuery}
              onSearch={(event) => setSearchQuery(event.currentTarget.value)}
              loading={isLoading}
              toolbarRightContent={
                <Button
                  variant="default"
                  leftSection={<IconDownload size={14} />}
                  onClick={handleExport}
                  loading={exporting}
                >
                  Export
                </Button>
              }
            />

            <Flex className={styles.footer} justify="space-between" align="center" mt="md">
              <Text className={styles.footerMeta}>
                {totalCount
                  ? `Showing ${showingFrom}–${showingTo} of ${totalCount} records`
                  : "Showing 0 records"}
              </Text>
              <Pagination
                total={totalPages}
                value={page}
                onChange={setPage}
                radius="sm"
                size="sm"
                siblings={1}
                withEdges
                disabled={isLoading || totalPages <= 1}
              />
            </Flex>
          </Tabs.Panel>
        </Tabs>
      </div>
    </Stack>
  );
};

export default SpecificOperatorWallet;
